"use client";

import React from "react";
import { motion } from "framer-motion";
import { SearchCheck, Milestone, ChartBar as BarChart3, ShieldCheck, ArrowRight, Sparkles, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const benefitsData = [{
  id: "clarity",
  number: "01",
  title: "Clarity on Current State",
  subtitle: "Complete Technical & Content Health Check",
  description: "Uncover hidden technical errors, indexation bottlenecks, and search penalties holding back your rankings. I deliver a transparent, jargon-free diagnostic of your entire website.",
  icon: SearchCheck,
  highlight: "Clear Diagnostic Findings"
}, {
  id: "roadmap",
  number: "02",
  title: "Prioritised Roadmap",
  subtitle: "Actionable High-Impact Execution Plan",
  description: "Stop guessing what to fix first. I provide a sequenced, step-by-step strategy categorized by business impact and implementation effort, tailored directly for your team.",
  icon: Milestone,
  highlight: "Clear ROI Priority"
}, {
  id: "benchmarking",
  number: "03",
  title: "Competitive Benchmarking",
  subtitle: "In-Depth Market & Competitor Dissection",
  description: "Understand relative strengths and weaknesses against your competitors, and identify gaps in their strategy you can act on.",
  icon: BarChart3,
  highlight: "Market Opportunity Gap"
}, {
  id: "cost-effective",
  number: "04",
  title: "Cost-Effective First Step",
  subtitle: "No Long-Term Retainer Commitments",
  description: "Gain immediate strategic direction. An independent audit gives you total clarity before investing in execution.",
  icon: ShieldCheck,
  highlight: "Clear Next Step"
}];
export default function AuditBenefits() {
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="auditbenefits" data-nav="light" className="relative py-20 lg:py-28 bg-background text-foreground overflow-hidden">
      <div className="absolute top-1/4 -left-32 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 -right-32 w-96 h-96 bg-accent/10 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20 space-y-4">
          <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold tracking-wide uppercase rounded-full bg-secondary text-secondary-foreground">
            <Sparkles className="w-3.5 h-3.5 mr-1 text-primary inline-block" />
            Strategic SEO Diagnostics
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-default tracking-tight text-foreground">
            Why an Independent SEO Audit is Essential
          </h2>

          <p className="text-base md:text-lg text-muted-foreground leading-relaxed font-default">
            Before spending budget on ongoing optimization, you need clear explanation and prioritised recommendations backed by an exact <Link to="/TechnicalSeoPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">technical SEO strategy</Link>. A structured diagnostic audit exposes hidden search barriers, isolates root causes, and reveals potential SEO opportunities.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {benefitsData.map((benefit, i) => {
          const Icon = benefit.icon;
          return <div data-index={i} key={benefit.id} className="h-full">
                <motion.div variants={itemVariants} className="h-full">
                  <Card className="h-full border border-border/80 bg-card hover:border-primary/40 hover:shadow-lg transition-all duration-300 rounded-xl flex flex-col justify-between overflow-hidden group">
                    <CardHeader className="p-6 md:p-8 space-y-4 pb-2">
                      <div className="flex items-center justify-between">
                        <div className="w-12 h-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                          <Icon className="w-6 h-6" />
                        </div>
                        <span className="text-3xl font-bold font-mono text-muted-foreground/40 group-hover:text-primary/30 transition-colors">
                          {benefit.number}
                        </span>
                      </div>

                      <div>
                        <span className="text-xs font-semibold uppercase tracking-wider text-primary font-default">
                          {benefit.subtitle}
                        </span>
                        <CardTitle className="text-xl md:text-2xl font-bold text-card-foreground font-default mt-1">
                          {benefit.title}
                        </CardTitle>
                      </div>
                    </CardHeader>

                    <CardContent className="p-6 md:p-8 pt-2 space-y-6 flex-1 flex flex-col justify-between">
                      <p className="text-muted-foreground text-sm md:text-base leading-relaxed font-default">
                        {benefit.description}
                      </p>

                      <div className="pt-4 border-t border-border/60 flex items-center justify-between text-xs font-medium text-foreground">
                        <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                          <CheckCircle2 className="w-4 h-4 text-primary" />
                          {benefit.highlight}
                        </span>
                        <span className="text-primary group-hover:translate-x-1 transition-transform inline-flex items-center gap-1 font-semibold">
                          Learn More
                          <ArrowRight className="w-3.5 h-3.5" />
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              </div>;
        })}
        </motion.div>

        <div className="mt-12 lg:mt-16 bg-muted/60 border border-border/80 rounded-2xl p-6 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6 shadow-sm">
          <div className="space-y-2 text-center md:text-left max-w-2xl">
            <h3 className="text-xl md:text-2xl font-bold text-foreground font-default">
              Ready to reveal what is stopping your search traffic?
            </h3>
            <p className="text-sm md:text-base text-muted-foreground font-default">
              Get clear explanation and prioritised recommendations directly from Yassine covering <Link to="/EcommerceSeoPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">ecommerce SEO</Link> and <Link to="/B2BSeoLeadGenerationPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">B2B SEO</Link>. Each review delivers detailed root-cause diagnosis, commercial impact scoring, and actionable remediation steps.
            </p>
          </div>

          <div className="shrink-0 flex flex-col sm:flex-row items-center gap-3">
            <Link to="/ContactPage">
              <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium px-6 py-3 rounded-lg shadow-md cursor-pointer transition-all flex items-center gap-2">
                Book a Free SEO Consultation
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-border/60 text-center md:text-left">
          <p className="text-sm text-muted-foreground leading-relaxed">
            <span className="font-semibold text-foreground mr-2">Explore Related SEO Services:</span>
            <Link to="/TechnicalSeoPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">Technical SEO support</Link>
            <span className="mx-2 text-muted-foreground/60">•</span>
            <Link to="/EcommerceSeoPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">Ecommerce SEO expertise</Link>
            <span className="mx-2 text-muted-foreground/60">•</span>
            <Link to="/B2BSeoLeadGenerationPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">B2B lead generation strategy</Link>
            <span className="mx-2 text-muted-foreground/60">•</span>
            <Link to="/LocalSEOPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">Local SEO strategy</Link>
            <span className="mx-2 text-muted-foreground/60">•</span>
            <Link to="/InternationalSeoPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">International SEO support</Link>
            <span className="mx-2 text-muted-foreground/60">•</span>
            <Link to="/AISearchOptimisationPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">AI search optimisation</Link>
          </p>
        </div>
      </div>
    </section>;
}